import type {
  AllergenInput,
  Category,
  Product,
  ProductDetail,
  ProductInput,
  ProductPage,
  ProductStatusFilter,
  ReferenceData,
  SessionInfo,
  BulkShelfLifeApprovalResult,
  DraftShelfLifeRuleForReview,
  ShelfLifeApprovalTarget,
  ShelfLifeDraftInput,
  ShelfLifeRevisionInput,
  ShelfLifeRule,
  IdentityAdministration,
  ManagedRole,
  ManagedRoleInput,
  OrganizationRoleAssignment,
  MemberInput,
  MemberRecord,
  MemberUpdateInput,
  LabelPreview,
  StandardLabelType,
  StorageState,
  ManagedRoleUpdateInput,
  SharedDeviceSwitch,
  SwitchableMember,
  Department,
  DepartmentInput,
  DepartmentUpdateInput,
  BridgeEnrolmentGrant,
  ManagedPrintBridge,
  PrintOptions,
  PrintProduct,
  OperationalPrintJobInput,
  ShadowPrintJobResult,
  ProductDatePreview,
  PrepList,
  PrepListEligibleProduct,
  PrepListPrintPreview,
  PpdsLabelTemplate,
  PpdsLabelPreview,
  PpdsLabelMode,
  PpdsDateMarkType,
  PpdsShelfLifeUnit,
  Printer,
  LabelTemplateOption,
  PrinterTemplateAssignment,
  PrintJobHistoryPage,
  PrintOutcome,
  OrganizationSettings,
  SiteSettings,
  BuffetMenu,
  BuffetMenuItem,
  BuffetEligibleProduct,
  BuffetLabelPreview,
  DashboardSnapshot,
  HealthStatus,
  PrinterSummary,
} from "./types";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly requestId: string | null,
  ) {
    super(message);
  }
}

export class KitchenHubApi {
  private switchToken: string | null = null;
  constructor(
    private readonly baseUrl: string,
    private readonly accessToken: string,
  ) {}

  restoreSwitchToken(token: string): void {
    this.switchToken = token;
  }

  session(): Promise<SessionInfo> {
    return this.request("/v1/session");
  }

  changeOwnPassword(password: string): Promise<void> {
    return this.request("/v1/session/password", { method: "POST", body: JSON.stringify({ password }) });
  }

  products(
    organizationId: string,
    cursor?: string,
    status: ProductStatusFilter = "active",
    siteId?: string,
  ): Promise<ProductPage> {
    const query = new URLSearchParams({ limit: "500", status });
    if (siteId) query.set("site_id", siteId);
    if (cursor) query.set("cursor", cursor);
    return this.request(`/v1/organizations/${organizationId}/products?${query}`);
  }

  async allProducts(
    organizationId: string,
    status: ProductStatusFilter = "active",
    siteId?: string,
  ): Promise<{ items: Product[]; containsAllergens: Record<string, string[]> }> {
    const items: Product[] = [];
    const containsAllergens: Record<string, string[]> = {};
    const seenCursors = new Set<string>();
    let cursor: string | undefined;
    do {
      const page = await this.products(organizationId, cursor, status, siteId);
      items.push(...page.items);
      Object.assign(containsAllergens, page.contains_allergens);
      cursor = page.next_cursor ?? undefined;
      if (cursor) {
        if (seenCursors.has(cursor)) throw new Error("Catalogue pagination repeated a cursor");
        seenCursors.add(cursor);
      }
    } while (cursor);
    return { items, containsAllergens };
  }

  product(organizationId: string, productId: string): Promise<ProductDetail> {
    return this.request(`/v1/organizations/${organizationId}/products/${productId}`);
  }

  duplicateProduct(organizationId: string, productId: string): Promise<ProductDetail> {
    return this.request(
      `/v1/organizations/${organizationId}/products/${productId}/duplicate`,
      { method: "POST" },
    );
  }

  labelPreview(
    organizationId: string,
    productId: string,
    labelType: StandardLabelType,
    storageState: StorageState,
    preparedOn: string,
  ): Promise<LabelPreview> {
    const query = new URLSearchParams({
      label_type: labelType,
      storage_state: storageState,
      prepared_on: preparedOn,
      quantity: "1",
    });
    return this.request(`/v1/organizations/${organizationId}/products/${productId}/label-preview?${query}`);
  }

  references(organizationId: string): Promise<ReferenceData> {
    return this.request(`/v1/organizations/${organizationId}/catalogue/reference-data`);
  }

  departments(
    organizationId: string,
    status: "active" | "inactive" | "all" = "all",
    search = "",
  ): Promise<Department[]> {
    const query = new URLSearchParams({ status });
    if (search.trim()) query.set("search", search.trim());
    return this.request(`/v1/organizations/${organizationId}/departments?${query}`);
  }

  createDepartment(organizationId: string, input: DepartmentInput): Promise<Department> {
    return this.request(`/v1/organizations/${organizationId}/departments`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  /**
   * Give an existing department to another site.
   *
   * Departments are organization-level and unique by code, so a second hotel
   * cannot create its own "Kitchen" - createDepartment refuses it as a
   * duplicate name. The site joins the department that already exists.
   */
  attachDepartment(
    organizationId: string,
    siteId: string,
    departmentId: string,
  ): Promise<{ site_department_id: string }> {
    return this.request(`/v1/organizations/${organizationId}/sites/${siteId}/departments`, {
      method: "POST",
      body: JSON.stringify({ department_id: departmentId }),
    });
  }

  /** Show or hide a department at one site, leaving other sites unchanged. */
  async setDepartmentVisibleAtSite(
    organizationId: string,
    department: Department,
    active: boolean,
  ): Promise<boolean> {
    const result = await this.request<{ active: boolean }>(
      `/v1/organizations/${organizationId}/sites/${department.site_id}/departments/${department.department_id}`,
      { method: "PUT", body: JSON.stringify({ active }) },
    );
    return result.active;
  }

  async updateDepartment(
    organizationId: string,
    department: Department,
    input: Omit<DepartmentUpdateInput, "expected_version">,
  ): Promise<Department> {
    const updated = await this.request<{
      name: string; code: string; active: boolean; version: number; sort_order?: number;
    }>(
      `/v1/organizations/${organizationId}/departments/${department.department_id}`,
      {
        method: "PUT",
        body: JSON.stringify({ ...input, expected_version: department.version }),
      },
    );
    return {
      ...department,
      name: updated.name,
      code: updated.code,
      active: updated.active,
      version: updated.version,
      sort_order: input.sort_order,
    };
  }

  deleteDepartment(organizationId: string, department: Department): Promise<void> {
    const query = new URLSearchParams({ expected_version: String(department.version) });
    return this.request(
      `/v1/organizations/${organizationId}/departments/${department.department_id}?${query}`,
      { method: "DELETE" },
    );
  }

  createCategory(organizationId: string, input: { name: string; sort_order?: number }): Promise<Category> {
    return this.request(`/v1/organizations/${organizationId}/categories`, {
      method: "POST",
      body: JSON.stringify({ name: input.name, sort_order: input.sort_order ?? 0 }),
    });
  }

  archiveProducts(organizationId: string, productIds: string[]): Promise<{ archived_ids: string[]; archived: number }> {
    return this.request(`/v1/organizations/${organizationId}/products/archive`, {
      method: "POST",
      body: JSON.stringify({ product_ids: productIds }),
    });
  }

  deleteCategory(organizationId: string, categoryId: string): Promise<void> {
    return this.request(`/v1/organizations/${organizationId}/categories/${categoryId}`, {
      method: "DELETE",
    });
  }

  listCategories(organizationId: string, status: ProductStatusFilter = "active"): Promise<Category[]> {
    return this.request(`/v1/organizations/${organizationId}/categories?status=${status}`);
  }

  updateCategory(
    organizationId: string,
    category: Category,
    input: { name: string; sort_order: number; active: boolean },
  ): Promise<Category> {
    return this.request(`/v1/organizations/${organizationId}/categories/${category.id}`, {
      method: "PUT",
      body: JSON.stringify({ ...input, expected_version: category.version }),
    });
  }

  createProduct(organizationId: string, input: ProductInput): Promise<Product> {
    return this.request(`/v1/organizations/${organizationId}/products`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  updateProduct(
    organizationId: string,
    product: Product,
    input: ProductInput,
    active = product.active,
  ): Promise<Product> {
    return this.request(`/v1/organizations/${organizationId}/products/${product.id}`, {
      method: "PUT",
      body: JSON.stringify({
        ...input,
        expected_version: product.version,
        active,
      }),
    });
  }

  setProductAllergen(
    organizationId: string,
    productId: string,
    allergenId: string,
    input: AllergenInput,
  ): Promise<void> {
    return this.request(
      `/v1/organizations/${organizationId}/products/${productId}/allergens/${allergenId}`,
      { method: "PUT", body: JSON.stringify(input) },
    );
  }

  removeProductAllergen(
    organizationId: string,
    productId: string,
    allergenId: string,
  ): Promise<void> {
    return this.request(
      `/v1/organizations/${organizationId}/products/${productId}/allergens/${allergenId}`,
      { method: "DELETE" },
    );
  }

  createShelfLifeDraft(
    organizationId: string,
    productId: string,
    input: ShelfLifeDraftInput,
  ): Promise<void> {
    return this.request(
      `/v1/organizations/${organizationId}/products/${productId}/shelf-life-rules`,
      { method: "POST", body: JSON.stringify(input) },
    );
  }

  approveShelfLifeRule(
    organizationId: string,
    productId: string,
    rule: Pick<ShelfLifeRule, "id" | "version">,
  ): Promise<ShelfLifeRule> {
    return this.request(
      `/v1/organizations/${organizationId}/products/${productId}/shelf-life-rules/${rule.id}/approve`,
      { method: "POST", body: JSON.stringify({ expected_version: rule.version }) },
    );
  }

  retireShelfLifeRule(
    organizationId: string,
    productId: string,
    rule: Pick<ShelfLifeRule, "id" | "version">,
  ): Promise<ShelfLifeRule> {
    return this.request(
      `/v1/organizations/${organizationId}/products/${productId}/shelf-life-rules/${rule.id}/retire`,
      { method: "POST", body: JSON.stringify({ expected_version: rule.version }) },
    );
  }

  reviseShelfLifeRule(
    organizationId: string,
    productId: string,
    ruleId: string,
    input: ShelfLifeRevisionInput,
  ): Promise<ShelfLifeRule> {
    return this.request(
      `/v1/organizations/${organizationId}/products/${productId}/shelf-life-rules/${ruleId}/revise`,
      { method: "POST", body: JSON.stringify(input) },
    );
  }

  draftShelfLifeRulesForReview(
    organizationId: string,
    siteDepartmentId?: string,
  ): Promise<DraftShelfLifeRuleForReview[]> {
    const query = new URLSearchParams({ only_with_duration: "true" });
    if (siteDepartmentId) query.set("site_department_id", siteDepartmentId);
    return this.request(
      `/v1/organizations/${organizationId}/shelf-life-rules/draft-review?${query}`,
    );
  }

  bulkApproveShelfLifeRules(
    organizationId: string,
    targets: ShelfLifeApprovalTarget[],
  ): Promise<BulkShelfLifeApprovalResult> {
    return this.request(
      `/v1/organizations/${organizationId}/shelf-life-rules/bulk-approve`,
      { method: "POST", body: JSON.stringify({ targets }) },
    );
  }

  identityAdministration(organizationId: string): Promise<IdentityAdministration> {
    return this.request(`/v1/organizations/${organizationId}/identity-administration`);
  }

  createRole(organizationId: string, input: ManagedRoleInput): Promise<ManagedRole> {
    return this.request(`/v1/organizations/${organizationId}/roles`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  assignOrganizationRole(
    organizationId: string,
    memberId: string,
    roleId: string,
    siteId?: string | null,
  ): Promise<OrganizationRoleAssignment> {
    return this.request(`/v1/organizations/${organizationId}/role-assignments`, {
      method: "POST",
      body: JSON.stringify({ member_id: memberId, role_id: roleId, site_id: siteId ?? null }),
    });
  }

  createMember(organizationId: string, input: MemberInput): Promise<MemberRecord> {
    return this.request(`/v1/organizations/${organizationId}/members`, { method: "POST", body: JSON.stringify(input) });
  }

  updateMember(organizationId: string, memberId: string, input: MemberUpdateInput): Promise<MemberRecord> {
    return this.request(`/v1/organizations/${organizationId}/members/${memberId}`, { method: "PUT", body: JSON.stringify(input) });
  }

  signOutMemberDevice(organizationId: string, memberId: string): Promise<void> {
    return this.request(`/v1/organizations/${organizationId}/members/${memberId}/sign-out-device`, { method: "POST" });
  }

  linkMemberAuth(organizationId: string, memberId: string, email: string, password?: string | null): Promise<MemberRecord & { account_created: boolean }> {
    return this.request(`/v1/organizations/${organizationId}/members/${memberId}/auth-link`, {
      method: "POST",
      body: JSON.stringify({ email, password: password ?? null }),
    });
  }

  resetMemberPassword(organizationId: string, memberId: string, password: string): Promise<{ member_id: string; email: string | null; sessions_revoked: boolean }> {
    return this.request(`/v1/organizations/${organizationId}/members/${memberId}/password-reset`, {
      method: "POST",
      body: JSON.stringify({ password }),
    });
  }

  updateRole(organizationId: string, roleId: string, input: ManagedRoleUpdateInput): Promise<ManagedRole> {
    return this.request(`/v1/organizations/${organizationId}/roles/${roleId}`, { method: "PUT", body: JSON.stringify(input) });
  }

  resetRolePermissions(organizationId: string, role: ManagedRole): Promise<ManagedRole> {
    return this.request(`/v1/organizations/${organizationId}/roles/${role.id}/reset-permissions`, { method: "POST", body: JSON.stringify({ expected_version: role.version }) });
  }

  deleteRole(organizationId: string, roleId: string): Promise<void> {
    return this.request(`/v1/organizations/${organizationId}/roles/${roleId}`, { method: "DELETE" });
  }

  revokeOrganizationRole(organizationId: string, assignmentId: string): Promise<void> {
    return this.request(`/v1/organizations/${organizationId}/role-assignments/${assignmentId}`, { method: "DELETE" });
  }

  switchableMembers(organizationId: string): Promise<SwitchableMember[]> {
    return this.request(`/v1/organizations/${organizationId}/shared-device/members`);
  }

  async switchSharedDeviceUser(organizationId: string, memberId: string, pin: string): Promise<SharedDeviceSwitch> {
    const switched = await this.request<SharedDeviceSwitch>(`/v1/organizations/${organizationId}/shared-device/switch`, { method: "POST", body: JSON.stringify({ member_id: memberId, pin }) });
    this.switchToken = switched.switch_token;
    return switched;
  }

  clearSharedDeviceUser(): void { this.switchToken = null; }

  createBridgeEnrolment(
    organizationId: string,
    input: { site_id: string; requested_name: string; ttl_minutes: number },
  ): Promise<BridgeEnrolmentGrant> {
    return this.request(`/v1/organizations/${organizationId}/print-bridge/enrolments`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  listPrintBridges(organizationId: string): Promise<ManagedPrintBridge[]> {
    return this.request(`/v1/organizations/${organizationId}/print-bridges`);
  }

  revokePrintBridge(organizationId: string, bridgeId: string): Promise<void> {
    return this.request(`/v1/organizations/${organizationId}/print-bridges/${bridgeId}`, {
      method: "DELETE",
    });
  }

  printOptions(organizationId: string, siteId: string): Promise<PrintOptions> {
    return this.request(`/v1/organizations/${organizationId}/sites/${siteId}/print-options`);
  }

  printProducts(
    organizationId: string, siteId: string, siteDepartmentId?: string,
  ): Promise<PrintProduct[]> {
    const query = new URLSearchParams();
    if (siteDepartmentId) query.set("site_department_id", siteDepartmentId);
    const suffix = query.size ? `?${query}` : "";
    return this.request(`/v1/organizations/${organizationId}/sites/${siteId}/print-products${suffix}`);
  }

  createOperationalPrintJob(
    organizationId: string, mode: "quick" | "batch", input: OperationalPrintJobInput,
  ): Promise<ShadowPrintJobResult> {
    return this.request(`/v1/organizations/${organizationId}/${mode}-print/jobs`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  batchDatePreviews(
    organizationId: string, siteId: string, siteDepartmentId: string,
    labelType: StandardLabelType, storageState: StorageState, preparedOn: string,
  ): Promise<ProductDatePreview[]> {
    const query = new URLSearchParams({
      site_department_id: siteDepartmentId, label_type: labelType,
      storage_state: storageState, prepared_on: preparedOn,
    });
    return this.request(`/v1/organizations/${organizationId}/sites/${siteId}/batch-print/date-previews?${query}`);
  }

  resetQuickPrintRanking(
    organizationId: string, siteId: string,
  ): Promise<{ affected_rows: number; history_preserved: true }> {
    return this.request(`/v1/organizations/${organizationId}/quick-print/ranking/reset`, {
      method: "POST", body: JSON.stringify({ site_id: siteId }),
    });
  }

  listPrepLists(
    organizationId: string, siteId: string, activeOnly = true,
  ): Promise<PrepList[]> {
    return this.request(
      `/v1/organizations/${organizationId}/sites/${siteId}/prep-lists?active_only=${activeOnly}`,
    );
  }

  getPrepList(organizationId: string, prepListId: string): Promise<PrepList> {
    return this.request(`/v1/organizations/${organizationId}/prep-lists/${prepListId}`);
  }

  createPrepList(
    organizationId: string,
    input: { site_id: string; name: string; site_department_id: string | null; base_covers: number },
  ): Promise<PrepList> {
    return this.request(`/v1/organizations/${organizationId}/prep-lists`, {
      method: "POST", body: JSON.stringify(input),
    });
  }

  updatePrepList(
    organizationId: string, prepListId: string,
    input: { expected_version: number; name: string; site_department_id: string | null; base_covers: number; active: boolean },
  ): Promise<PrepList> {
    return this.request(`/v1/organizations/${organizationId}/prep-lists/${prepListId}`, {
      method: "PUT", body: JSON.stringify(input),
    });
  }

  deletePrepList(organizationId: string, prepListId: string): Promise<void> {
    return this.request(`/v1/organizations/${organizationId}/prep-lists/${prepListId}`, {
      method: "DELETE",
    });
  }

  addPrepListItem(
    organizationId: string, prepListId: string,
    input: { product_id: string; storage_state: StorageState; portions_per_cover: number; tray_capacity: number; sort_order: number },
  ): Promise<PrepList["items"][number]> {
    return this.request(`/v1/organizations/${organizationId}/prep-lists/${prepListId}/items`, {
      method: "POST", body: JSON.stringify(input),
    });
  }

  updatePrepListItem(
    organizationId: string, prepListId: string, productId: string,
    input: { product_id: string; storage_state: StorageState; portions_per_cover: number; tray_capacity: number; sort_order: number },
  ): Promise<PrepList["items"][number]> {
    return this.request(`/v1/organizations/${organizationId}/prep-lists/${prepListId}/items/${productId}`, {
      method: "PUT", body: JSON.stringify(input),
    });
  }

  removePrepListItem(organizationId: string, prepListId: string, productId: string): Promise<void> {
    return this.request(`/v1/organizations/${organizationId}/prep-lists/${prepListId}/items/${productId}`, {
      method: "DELETE",
    });
  }

  prepListEligibleProducts(
    organizationId: string, siteId: string, siteDepartmentId?: string,
  ): Promise<PrepListEligibleProduct[]> {
    const query = new URLSearchParams();
    if (siteDepartmentId) query.set("site_department_id", siteDepartmentId);
    const suffix = query.size ? `?${query}` : "";
    return this.request(`/v1/organizations/${organizationId}/sites/${siteId}/prep-lists/eligible-products${suffix}`);
  }

  previewPrepListPrint(
    organizationId: string, prepListId: string, actualCovers: number, preparedOn: string,
  ): Promise<PrepListPrintPreview> {
    const query = new URLSearchParams({
      actual_covers: String(actualCovers), prepared_on: preparedOn,
    });
    return this.request(`/v1/organizations/${organizationId}/prep-lists/${prepListId}/print-preview?${query}`);
  }

  printPrepList(
    organizationId: string, prepListId: string,
    input: {
      site_id: string; printer_id: string; idempotency_key: string;
      prepared_on: string; actual_covers: number; selected_product_ids: string[];
    },
  ): Promise<ShadowPrintJobResult> {
    return this.request(`/v1/organizations/${organizationId}/prep-lists/${prepListId}/print`, {
      method: "POST", body: JSON.stringify(input),
    });
  }

  listPpdsTemplates(
    organizationId: string, siteId: string, activeOnly = true,
  ): Promise<PpdsLabelTemplate[]> {
    return this.request(
      `/v1/organizations/${organizationId}/sites/${siteId}/ppds-label-templates?active_only=${activeOnly}`,
    );
  }

  getPpdsTemplate(organizationId: string, templateId: string): Promise<PpdsLabelTemplate> {
    return this.request(`/v1/organizations/${organizationId}/ppds-label-templates/${templateId}`);
  }

  createPpdsTemplate(
    organizationId: string,
    input: {
      site_id: string; food_name: string; label_mode: PpdsLabelMode; ingredients: string;
      allergen_ids: string[]; net_quantity: string | null; date_mark_type: PpdsDateMarkType;
      shelf_life_value: number | null; shelf_life_unit: PpdsShelfLifeUnit | null;
      storage_instructions: string | null; reheating_instructions: string | null;
      additional_warnings: string | null;
    },
  ): Promise<PpdsLabelTemplate> {
    return this.request(`/v1/organizations/${organizationId}/ppds-label-templates`, {
      method: "POST", body: JSON.stringify(input),
    });
  }

  updatePpdsTemplate(
    organizationId: string, templateId: string,
    input: {
      expected_version: number; food_name: string; label_mode: PpdsLabelMode;
      ingredients: string; allergen_ids: string[]; net_quantity: string | null;
      date_mark_type: PpdsDateMarkType; shelf_life_value: number | null;
      shelf_life_unit: PpdsShelfLifeUnit | null; storage_instructions: string | null;
      reheating_instructions: string | null; additional_warnings: string | null; active: boolean;
    },
  ): Promise<PpdsLabelTemplate> {
    return this.request(`/v1/organizations/${organizationId}/ppds-label-templates/${templateId}`, {
      method: "PUT", body: JSON.stringify(input),
    });
  }

  deletePpdsTemplate(organizationId: string, templateId: string): Promise<void> {
    return this.request(`/v1/organizations/${organizationId}/ppds-label-templates/${templateId}`, {
      method: "DELETE",
    });
  }

  setSiteFoodBusinessIdentity(
    organizationId: string, siteId: string,
    input: { expected_version: number; food_business_name: string | null; food_business_address: string | null },
  ): Promise<{ status: string }> {
    return this.request(`/v1/organizations/${organizationId}/sites/${siteId}/food-business-identity`, {
      method: "PUT", body: JSON.stringify(input),
    });
  }

  previewPpdsTemplate(
    organizationId: string, templateId: string, preparedAt: string,
  ): Promise<PpdsLabelPreview> {
    const query = new URLSearchParams({ prepared_at: preparedAt });
    return this.request(`/v1/organizations/${organizationId}/ppds-label-templates/${templateId}/preview?${query}`);
  }

  printPpdsTemplate(
    organizationId: string, templateId: string,
    input: { site_id: string; printer_id: string; idempotency_key: string; quantity: number; prepared_at: string },
  ): Promise<ShadowPrintJobResult> {
    return this.request(`/v1/organizations/${organizationId}/ppds-label-templates/${templateId}/print`, {
      method: "POST", body: JSON.stringify(input),
    });
  }

  listPrinters(organizationId: string, siteId: string): Promise<Printer[]> {
    return this.request(`/v1/organizations/${organizationId}/sites/${siteId}/printers`);
  }

  renamePrinter(
    organizationId: string, siteId: string, printerId: string,
    input: { display_name: string | null; expected_version: number },
  ): Promise<Printer> {
    return this.request(
      `/v1/organizations/${organizationId}/sites/${siteId}/printers/${printerId}/name`,
      { method: "PUT", body: JSON.stringify(input) },
    );
  }

  setPrinterActive(
    organizationId: string, siteId: string, printerId: string,
    input: { active: boolean; expected_version: number },
  ): Promise<Printer> {
    return this.request(
      `/v1/organizations/${organizationId}/sites/${siteId}/printers/${printerId}/active`,
      { method: "PUT", body: JSON.stringify(input) },
    );
  }

  listLabelTemplateOptions(organizationId: string, siteId: string): Promise<LabelTemplateOption[]> {
    return this.request(`/v1/organizations/${organizationId}/sites/${siteId}/label-templates`);
  }

  listPrinterTemplateAssignments(
    organizationId: string, siteId: string, printerId: string,
  ): Promise<PrinterTemplateAssignment[]> {
    return this.request(
      `/v1/organizations/${organizationId}/sites/${siteId}/printers/${printerId}/template-assignments`,
    );
  }

  createPrinterTemplateAssignment(
    organizationId: string, siteId: string, printerId: string,
    input: { label_type_id: string; template_id: string; priority: number },
  ): Promise<{ id: string }> {
    return this.request(
      `/v1/organizations/${organizationId}/sites/${siteId}/printers/${printerId}/template-assignments`,
      { method: "POST", body: JSON.stringify(input) },
    );
  }

  updatePrinterTemplateAssignment(
    organizationId: string, siteId: string, assignmentId: string,
    input: { priority: number; active: boolean },
  ): Promise<void> {
    return this.request(
      `/v1/organizations/${organizationId}/sites/${siteId}/printer-template-assignments/${assignmentId}`,
      { method: "PUT", body: JSON.stringify(input) },
    );
  }

  deletePrinterTemplateAssignment(
    organizationId: string, siteId: string, assignmentId: string,
  ): Promise<void> {
    return this.request(
      `/v1/organizations/${organizationId}/sites/${siteId}/printer-template-assignments/${assignmentId}`,
      { method: "DELETE" },
    );
  }

  searchPrintHistory(
    organizationId: string, siteId: string,
    filters: {
      printerId?: string; sourceType?: string; status?: string; productId?: string;
      createdFrom?: string; createdTo?: string;
      beforeCreatedAt?: string; beforeId?: string; limit?: number;
    } = {},
  ): Promise<PrintJobHistoryPage> {
    const params = new URLSearchParams();
    if (filters.printerId) params.set("printer_id", filters.printerId);
    if (filters.sourceType) params.set("source_type", filters.sourceType);
    if (filters.status) params.set("status", filters.status);
    if (filters.productId) params.set("product_id", filters.productId);
    if (filters.createdFrom) params.set("created_from", filters.createdFrom);
    if (filters.createdTo) params.set("created_to", filters.createdTo);
    if (filters.beforeCreatedAt) params.set("before_created_at", filters.beforeCreatedAt);
    if (filters.beforeId) params.set("before_id", filters.beforeId);
    params.set("limit", String(filters.limit ?? 25));
    return this.request(
      `/v1/organizations/${organizationId}/sites/${siteId}/print-jobs?${params.toString()}`,
    );
  }

  async exportPrintHistoryCsv(
    organizationId: string, siteId: string,
    filters: {
      printerId?: string; sourceType?: string; status?: string; productId?: string;
      createdFrom?: string; createdTo?: string;
    } = {},
  ): Promise<Blob> {
    const params = new URLSearchParams();
    if (filters.printerId) params.set("printer_id", filters.printerId);
    if (filters.sourceType) params.set("source_type", filters.sourceType);
    if (filters.status) params.set("status", filters.status);
    if (filters.productId) params.set("product_id", filters.productId);
    if (filters.createdFrom) params.set("created_from", filters.createdFrom);
    if (filters.createdTo) params.set("created_to", filters.createdTo);
    const response = await fetch(
      `${this.baseUrl}/v1/organizations/${organizationId}/sites/${siteId}/print-jobs/export?${params.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${this.accessToken}`,
          ...(this.switchToken ? { "X-Kitchen-Hub-Switch-Token": this.switchToken } : {}),
        },
      },
    );
    if (!response.ok) {
      throw new ApiError(
        "Kitchen Hub could not export print history.",
        response.status,
        response.headers.get("x-request-id"),
      );
    }
    return response.blob();
  }

  async exportProductsCsv(organizationId: string, siteId?: string): Promise<Blob> {
    const query = siteId ? `?site_id=${encodeURIComponent(siteId)}` : "";
    const response = await fetch(
      `${this.baseUrl}/v1/organizations/${organizationId}/products/export${query}`,
      {
        headers: {
          Authorization: `Bearer ${this.accessToken}`,
          ...(this.switchToken ? { "X-Kitchen-Hub-Switch-Token": this.switchToken } : {}),
        },
      },
    );
    if (!response.ok) {
      throw new ApiError(
        "Kitchen Hub could not export the catalogue.",
        response.status,
        response.headers.get("x-request-id"),
      );
    }
    return response.blob();
  }

  reprintPrintJob(
    organizationId: string, jobId: string, idempotencyKey: string,
  ): Promise<{ job_id: string; status: string; shadow: boolean; physical_printing_enabled: boolean }> {
    return this.request(`/v1/organizations/${organizationId}/print-jobs/${jobId}/reprint`, {
      method: "POST", body: JSON.stringify({ idempotency_key: idempotencyKey }),
    });
  }

  confirmPrintOutcome(
    organizationId: string, jobId: string, printed: boolean,
  ): Promise<{ job_id: string; confirmed_outcome: PrintOutcome }> {
    return this.request(`/v1/organizations/${organizationId}/print-jobs/${jobId}/confirm-outcome`, {
      method: "POST", body: JSON.stringify({ printed }),
    });
  }

  getOrganizationSettings(organizationId: string): Promise<OrganizationSettings> {
    return this.request(`/v1/organizations/${organizationId}/settings`);
  }

  updateOrganizationInactivityTimeout(
    organizationId: string, input: { expected_version: number; inactivity_timeout_minutes: number | null },
  ): Promise<OrganizationSettings> {
    return this.request(`/v1/organizations/${organizationId}/settings/inactivity-timeout`, {
      method: "PUT", body: JSON.stringify(input),
    });
  }

  listSites(organizationId: string, activeOnly = true): Promise<SiteSettings[]> {
    return this.request(`/v1/organizations/${organizationId}/sites?active_only=${activeOnly}`);
  }

  createSite(
    organizationId: string, input: { name: string; code: string; timezone: string },
  ): Promise<SiteSettings> {
    return this.request(`/v1/organizations/${organizationId}/sites`, {
      method: "POST", body: JSON.stringify(input),
    });
  }

  getSiteSettings(organizationId: string, siteId: string): Promise<SiteSettings> {
    return this.request(`/v1/organizations/${organizationId}/sites/${siteId}`);
  }

  updateSiteDetails(
    organizationId: string, siteId: string,
    input: { name: string; code: string; expected_version: number },
  ): Promise<SiteSettings> {
    return this.request(`/v1/organizations/${organizationId}/sites/${siteId}/details`, {
      method: "PUT", body: JSON.stringify(input),
    });
  }

  updateSitePrintSettings(
    organizationId: string, siteId: string,
    input: { expected_version: number; default_printer_id: string | null; label_width_mm: number; label_height_mm: number },
  ): Promise<SiteSettings> {
    return this.request(`/v1/organizations/${organizationId}/sites/${siteId}/print-settings`, {
      method: "PUT", body: JSON.stringify(input),
    });
  }

  listBuffetMenus(organizationId: string, siteId: string, activeOnly = true): Promise<BuffetMenu[]> {
    return this.request(
      `/v1/organizations/${organizationId}/sites/${siteId}/buffet-menus?active_only=${activeOnly}`,
    );
  }

  getBuffetMenu(organizationId: string, menuId: string): Promise<BuffetMenu> {
    return this.request(`/v1/organizations/${organizationId}/buffet-menus/${menuId}`);
  }

  listBuffetEligibleProducts(organizationId: string): Promise<BuffetEligibleProduct[]> {
    return this.request(`/v1/organizations/${organizationId}/buffet-eligible-products`);
  }

  createBuffetMenu(organizationId: string, input: { site_id: string; name: string }): Promise<BuffetMenu> {
    return this.request(`/v1/organizations/${organizationId}/buffet-menus`, {
      method: "POST", body: JSON.stringify(input),
    });
  }

  updateBuffetMenu(
    organizationId: string, menuId: string,
    input: { expected_version: number; name: string; active: boolean },
  ): Promise<BuffetMenu> {
    return this.request(`/v1/organizations/${organizationId}/buffet-menus/${menuId}`, {
      method: "PUT", body: JSON.stringify(input),
    });
  }

  deleteBuffetMenu(organizationId: string, menuId: string): Promise<void> {
    return this.request(`/v1/organizations/${organizationId}/buffet-menus/${menuId}`, {
      method: "DELETE",
    });
  }

  addBuffetMenuItem(
    organizationId: string, menuId: string, input: { product_id: string; sort_order?: number },
  ): Promise<BuffetMenuItem> {
    return this.request(`/v1/organizations/${organizationId}/buffet-menus/${menuId}/items`, {
      method: "POST", body: JSON.stringify(input),
    });
  }

  removeBuffetMenuItem(organizationId: string, menuId: string, productId: string): Promise<void> {
    return this.request(
      `/v1/organizations/${organizationId}/buffet-menus/${menuId}/items/${productId}`,
      { method: "DELETE" },
    );
  }

  previewBuffetItem(organizationId: string, menuId: string, productId: string): Promise<BuffetLabelPreview> {
    return this.request(
      `/v1/organizations/${organizationId}/buffet-menus/${menuId}/items/${productId}/preview`,
    );
  }

  printBuffetMenuItems(
    organizationId: string, menuId: string,
    input: {
      site_id: string; printer_id: string; idempotency_key: string;
      selections: { product_id: string; quantity: number }[];
    },
  ): Promise<{ job_id: string; status: string; shadow: boolean; label_count: number; physical_printing_enabled: boolean }> {
    return this.request(`/v1/organizations/${organizationId}/buffet-menus/${menuId}/print`, {
      method: "POST", body: JSON.stringify(input),
    });
  }

  getDashboard(organizationId: string, siteId: string): Promise<DashboardSnapshot> {
    return this.request(`/v1/organizations/${organizationId}/sites/${siteId}/dashboard`);
  }

  getHealth(): Promise<HealthStatus> {
    return this.request("/health/ready");
  }

  getPrinterSummary(organizationId: string, siteId: string): Promise<PrinterSummary> {
    return this.request(`/v1/organizations/${organizationId}/sites/${siteId}/printer-summary`);
  }

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const response = await fetch(`${this.baseUrl}${path}`, {
      ...init,
      headers: {
        Authorization: `Bearer ${this.accessToken}`,
        "Content-Type": "application/json",
        ...(this.switchToken ? { "X-Kitchen-Hub-Switch-Token": this.switchToken } : {}),
        ...init.headers,
      },
    });
    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as { detail?: string } | null;
      throw new ApiError(
        body?.detail ?? "Kitchen Hub could not complete the request.",
        response.status,
        response.headers.get("x-request-id"),
      );
    }
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }
}
